import React from 'react'
import styled, { keyframes } from 'styled-components'
import ShieldRoundedIcon          from '@mui/icons-material/ShieldRounded'
import SettingsRoundedIcon        from '@mui/icons-material/SettingsRounded'
import AccessTimeRoundedIcon      from '@mui/icons-material/AccessTimeRounded'
import LocalFireDepartmentRounded from '@mui/icons-material/LocalFireDepartmentRounded'
import WaterDropRoundedIcon       from '@mui/icons-material/WaterDropRounded'
import HelpOutlineRoundedIcon     from '@mui/icons-material/HelpOutlineRounded'
import { scoreColour, formatPercent } from './scoreColour'
import { ink, surface, line, radius, shadow, font, transition, cardStyle } from 'features/dashboard/tokens'

const DOMAIN_ICONS = {
  shield:   ShieldRoundedIcon,
  settings: SettingsRoundedIcon,
  clock:    AccessTimeRoundedIcon,
  alert:    LocalFireDepartmentRounded,
  water:    WaterDropRoundedIcon,
}

/**
 * BP × Domain heatmap. Each cell is the score of that entity for that
 * domain, with a duotone gradient fill keyed by the band and the column's
 * TaskDomain colour. Cells animate in on mount; rows highlight on hover;
 * cells lift on hover and reveal an overdue badge when present.
 *
 * Props:
 *   rows: [{ id, name, type, breakdown: [{ task_domain_id, score, overdue, total_tasks }] }]
 *   domains: [{ id, short, name, colour, icon }]
 *   onCellClick: ({ row, domain, value }) => void
 *   onRowDrill: (row) => void  — fired on the right-arrow click (deeper altitude)
 *   onRowFocus: (row) => void  — fired on the row NAME click (same altitude, just this entity)
 */
const ComplianceMatrix = ({ rows = [], domains = [], onCellClick, onRowDrill, onRowFocus }) => {
  if (!rows.length || !domains.length) {
    return <Empty>No compliance data to map yet.</Empty>
  }
  return (
    <TableWrap>
      <Table>
        <thead>
          <tr>
            <RowHead />
            {domains.map(d => {
              const Icon = DOMAIN_ICONS[d.icon] || HelpOutlineRoundedIcon
              const tone = d.colour || '#33becc'
              return (
                <ColHead key={d.id} title={d.name}>
                  <ColHeadInner $tone={tone}>
                    <ColIcon $tone={tone}><Icon fontSize='small' /></ColIcon>
                    <ColLabel>{d.short || d.name}</ColLabel>
                  </ColHeadInner>
                </ColHead>
              )
            })}
          </tr>
        </thead>
        <tbody>
          {rows.map((row, ri) => (
            <Row key={row.id} $stagger={ri}>
              <RowHead>
                <RowHeadActions>
                  {onRowFocus ? (
                    <RowFocus onClick={() => onRowFocus(row)} title='Focus on this row'>
                      <RowName>{row.name}</RowName>
                    </RowFocus>
                  ) : (
                    <RowName>{row.name}</RowName>
                  )}
                  {onRowDrill && (
                    <DrillButton onClick={() => onRowDrill(row)} title='Drill in to the next level'>
                      <DrillArrow>→</DrillArrow>
                    </DrillButton>
                  )}
                </RowHeadActions>
              </RowHead>
              {domains.map((d, di) => {
                const cell = (row.breakdown || []).find(b => b.task_domain_id === d.id)
                const score = cell ? cell.score : null
                const score0 = score === null || score === undefined ? null : score
                const overdue = cell?.overdue ?? 0
                const colour = scoreColour(score0)
                const tone = d.colour || colour
                return (
                  <Cell key={d.id}
                    $stagger={ri * domains.length + di}
                    onClick={() => onCellClick?.({ row, domain: d, value: cell })}
                    $clickable={!!onCellClick}>
                    {score0 === null ? (
                      <EmptyCell />
                    ) : (
                      <CellInner
                        $colour={colour}
                        $tone={tone}
                        $score={score0}
                      >
                        <ScoreNumber>{formatPercent(score0)}</ScoreNumber>
                        {overdue > 0 && (
                          <OverdueBadge title={`${overdue} overdue`}>
                            {overdue}
                          </OverdueBadge>
                        )}
                      </CellInner>
                    )}
                  </Cell>
                )
              })}
            </Row>
          ))}
        </tbody>
      </Table>

      <Legend>
        <LegendItem>
          <LegendChip style={{ background: '#b0002033', borderColor: '#b0002060' }} />
          <span>Critical · below 50%</span>
        </LegendItem>
        <LegendItem>
          <LegendChip style={{ background: '#d9770633', borderColor: '#d9770680' }} />
          <span>Attention · 50–74%</span>
        </LegendItem>
        <LegendItem>
          <LegendChip style={{ background: '#15803d33', borderColor: '#15803d80' }} />
          <span>On track · 75–94%</span>
        </LegendItem>
        <LegendItem>
          <LegendChip style={{ background: '#15803d66', borderColor: '#15803d' }} />
          <span>Compliant · 95%+</span>
        </LegendItem>
      </Legend>
    </TableWrap>
  )
}

export default ComplianceMatrix

const fadeUp = keyframes`
  from { opacity: 0; transform: translateY(6px); }
  to   { opacity: 1; transform: translateY(0); }
`
const popIn = keyframes`
  0%   { opacity: 0; transform: scale(0.86); }
  60%  { opacity: 1; transform: scale(1.04); }
  100% { opacity: 1; transform: scale(1.0); }
`

const Empty = styled.div`
  min-height: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 22px;
  font-size: ${font.body};
  color: ${({ theme }) => theme.colors.grey[800]};
  border: 1px dashed ${line.base};
  border-radius: ${radius.md};
  text-align: center;
`

const TableWrap = styled.div`
  ${cardStyle}
  padding: 18px 18px 12px;
  overflow-x: auto;
`

const Table = styled.table`
  width: 100%;
  border-collapse: separate;
  border-spacing: 6px 6px;
`

const Row = styled.tr`
  animation: ${fadeUp} 380ms ease both;
  animation-delay: ${({ $stagger }) => 40 + $stagger * 28}ms;

  &:hover td > div {
    box-shadow: ${shadow.lift};
  }
`

const RowHead = styled.th`
  text-align: left;
  padding: 0 12px;
  font-size: ${font.small};
  font-weight: 600;
  color: ${({ theme }) => theme.colors.haiti};
  white-space: nowrap;
  vertical-align: middle;
`

const RowName = styled.span`
  display: inline-block;
  padding: 2px 0;
`

const RowHeadActions = styled.div`
  display: inline-flex; align-items: center; gap: 2px;
`

const RowFocus = styled.button`
  font: inherit;
  display: inline-flex; align-items: center; gap: 6px;
  background: transparent;
  border: none;
  color: inherit;
  cursor: pointer;
  font-weight: 600;
  padding: 4px 8px;
  border-radius: ${radius.sm};
  transition: ${transition};
  &:hover {
    background: rgba(51, 190, 204, 0.10);
    color: #1f8c97;
  }
`

const DrillButton = styled.button`
  font: inherit;
  display: inline-flex; align-items: center; justify-content: center;
  width: 24px; height: 24px;
  background: transparent;
  border: 1px solid transparent;
  color: ${ink.mute};
  cursor: pointer;
  border-radius: ${radius.sm};
  opacity: 0.45;
  transition: ${transition};
  &:hover {
    opacity: 1;
    background: ${surface.wash};
    color: ${ink.base};
    border-color: ${line.base};
  }
`

const DrillArrow = styled.span`
  font-weight: 800;
  font-size: ${font.body};
  line-height: 1;
`

const ColHead = styled.th`
  vertical-align: bottom;
  padding: 0 2px 6px;
  text-align: center;
`

const ColHeadInner = styled.div`
  display: inline-flex;
  flex-direction: column;
  align-items: center;
  gap: 6px;
`

const ColIcon = styled.span`
  display: inline-flex; align-items: center; justify-content: center;
  width: 30px; height: 30px;
  border-radius: ${radius.round};
  background: ${({ $tone }) => `${$tone}1A`};
  color: ${({ $tone }) => $tone};
  border: 1px solid ${({ $tone }) => `${$tone}33`};
  transition: transform 160ms ease;
  &:hover { transform: translateY(-1px) scale(1.06); }
`

const ColLabel = styled.span`
  font-size: ${font.caption};
  font-weight: 700;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: ${({ theme }) => theme.colors.grey[800]};
  font-family: 'FuturaPT-Light', system-ui, sans-serif;
`

const Cell = styled.td`
  padding: 0;
  cursor: ${({ $clickable }) => ($clickable ? 'pointer' : 'default')};
`

const CellInner = styled.div`
  position: relative;
  min-width: 60px;
  height: 42px;
  border-radius: ${radius.md};
  display: inline-flex; align-items: center; justify-content: center;
  background: ${({ $colour }) => `${$colour}26`};
  border: 1px solid ${({ $colour }) => `${$colour}55`};
  color: ${({ $colour }) => $colour};
  font-weight: 800;
  font-size: ${font.body};
  letter-spacing: -0.01em;
  width: 100%;
  transition: ${transition};
  animation: ${popIn} 420ms cubic-bezier(0.34, 1.32, 0.64, 1) both;
  animation-delay: ${({ $stagger }) => 80 + $stagger * 14}ms;

  ${Cell}:hover & {
    transform: translateY(-2px);
    border-color: ${({ $colour }) => $colour};
  }
`

const ScoreNumber = styled.span``

const OverdueBadge = styled.span`
  position: absolute;
  top: -6px; right: -6px;
  min-width: 18px; height: 18px;
  padding: 0 5px;
  border-radius: ${radius.pill};
  background: #b00020;
  color: #fff;
  font-size: ${font.caption};
  font-weight: 800;
  display: inline-flex; align-items: center; justify-content: center;
  box-shadow: ${shadow.lift};
  border: 1.5px solid #fff;
  letter-spacing: 0.02em;
`

const EmptyCell = styled.div`
  min-width: 60px;
  width: 100%;
  height: 42px;
  border-radius: ${radius.md};
  border: 1px dashed ${line.base};
  background: repeating-linear-gradient(
    135deg,
    transparent 0 6px,
    rgba(12, 18, 41, 0.02) 6px 12px
  );
`

const Legend = styled.div`
  margin-top: 16px;
  padding-top: 12px;
  border-top: 1px solid ${line.base};
  display: flex; flex-wrap: wrap; gap: 16px;
  font-size: ${font.caption};
  color: ${({ theme }) => theme.colors.grey[800]};
`

const LegendItem = styled.div`
  display: inline-flex; align-items: center; gap: 6px;
`

const LegendChip = styled.span`
  width: 18px; height: 12px;
  border-radius: ${radius.xs};
  border: 1px solid;
  display: inline-block;
`
